import { Injectable } from "@angular/core";
import { HttpClientService } from "../../common/http-client.service";
import { Headers, RequestOptions, ResponseContentType } from "@angular/http";
import { Observable } from "rxjs/Rx";
import "rxjs/add/operator/map";
import { CommonService } from "../../common/common.service";
import { environment } from "../../../environments/environment";

@Injectable()
export class AgentRequestsService {
  constructor(private http: HttpClientService, private common: CommonService) {}

  private mapAgentRequestsResponse(res: any) {
    if (res && res.status === "success") {
      let requests = [];

      // Handle the new response structure where object is a JSON string
      // containing { result: {...}, data: [...] }
      if (res.object) {
        if (typeof res.object === "string") {
          try {
            const parsed = JSON.parse(res.object);
            // Check if parsed object has a 'data' property with array
            if (parsed && parsed.data && Array.isArray(parsed.data)) {
              requests = parsed.data;
            } else if (Array.isArray(parsed)) {
              requests = parsed;
            }
          } catch (e) {
            requests = [];
          }
        } else if (Array.isArray(res.object)) {
          requests = res.object;
        } else if (res.object.data && Array.isArray(res.object.data)) {
          // Handle case where object is already parsed and has data property
          requests = res.object.data;
        }
      } else if (res.data) {
        if (typeof res.data === "string") {
          try {
            const parsed = JSON.parse(res.data);
            if (parsed && parsed.data && Array.isArray(parsed.data)) {
              requests = parsed.data;
            } else if (Array.isArray(parsed)) {
              requests = parsed;
            }
          } catch (e) {
            requests = [];
          }
        } else if (Array.isArray(res.data)) {
          requests = res.data;
        } else if (res.data.data && Array.isArray(res.data.data)) {
          requests = res.data.data;
        }
      }

      if (!Array.isArray(requests)) {
        requests = [];
      }

      requests = requests.map((item: any) => {
        if (item.createdAt) {
          item.requestDate = item.createdAt;
        }
        if (item.phoneNumber) {
          item.phone = item.phoneNumber;
        }
        return item;
      });

      const pendingCount = requests.filter(
        (item: any) => item.status === "PENDING" || item.status === "pending"
      ).length;

      return {
        status: "success",
        object: requests,
        totalCount: requests.length,
        pendingCount: pendingCount,
      };
    }

    return {
      status: (res && res.status) || "failed",
      object: [],
      totalCount: 0,
      pendingCount: 0,
    };
  }

  getAgentRequests(req): Observable<any> {
    let params: any = {};

    // Only send status if it's not empty (don't send "ALL" for this API)
    if (
      req.status &&
      req.status !== "" &&
      String(req.status).trim().length > 0
    ) {
      params.status = req.status;
    }

    if (req.searchText && req.searchText !== "") {
      params.searchText = req.searchText;
    }

    if (req.fromDate) {
      params.fromDate = req.fromDate;
    }

    if (req.toDate) {
      params.toDate = req.toDate;
    }

    if (Object.keys(params).length === 0) {
      params = null;
    }

    return this.http
      .get("adminWallet/myRequests", params, 32)
      .map((res: any) => this.mapAgentRequestsResponse(res));
  }

  getAgentRequestsByRange(req): Observable<any> {
    const formData = new FormData();

    // When status dropdown is "All" (empty string/null), send "ALL" to backend
    if (req.status !== undefined && req.status !== null) {
      const statusValue =
        req.status !== "" && String(req.status).trim().length > 0
          ? req.status
          : "ALL";
      formData.append("status", statusValue);
    }

    if (req.fromDate) {
      formData.append("fromDate", req.fromDate);
    }

    if (req.toDate) {
      formData.append("toDate", req.toDate);
    }

    return Observable.create((observer: any) => {
      const xhr = new XMLHttpRequest();
      const apiEndpoint = environment.MFS_WALLET_MANAGEMENT_ENDPOINT;
      const url = `${apiEndpoint}adminWallet/myRequestsByRange`;

      xhr.open("POST", url, true);

      const token = this.common.token || localStorage.getItem("tkn");
      if (token) {
        xhr.setRequestHeader(this.common.authToken, token);
      }
      xhr.setRequestHeader("locale", "en");
      xhr.setRequestHeader("language", "ENG");

      xhr.onreadystatechange = () => {
        if (xhr.readyState === 4) {
          if (xhr.status === 200) {
            try {
              const response = JSON.parse(xhr.responseText);
              const mappedResponse = this.mapAgentRequestsResponse(response);
              observer.next(mappedResponse);
              observer.complete();
            } catch (e) {
              observer.error(new Error("Failed to parse response"));
            }
          } else {
            try {
              const errorResponse = JSON.parse(xhr.responseText);
              observer.error(errorResponse);
            } catch (e) {
              observer.error(
                new Error(`Request failed with status ${xhr.status}`)
              );
            }
          }
        }
      };

      xhr.onerror = () => {
        observer.error(new Error("Network error"));
      };

      xhr.send(formData);
    });
  }

  // Approve request
  approveRequest(
    agentId: string,
    comments: string,
    approvedAmount?: number,
    transactionId?: string
  ): Observable<any> {
    const userInfo = this.common.getUserInfo();
    const approvedBy =
      userInfo && userInfo.userName ? userInfo.userName : "admin";

    const params = {
      approvedAmount: approvedAmount !== undefined ? approvedAmount : 0,
      comments: comments,
      approvedBy: approvedBy,
      transactionId: transactionId || "",
    };
    return this.http.put(`adminWallet/approve/${agentId}`, params, 32);
  }

  rejectLegacyRequest(
    agentId: string,
    comments: string,
    transactionId?: string
  ): Observable<any> {
    const userInfo = this.common.getUserInfo();
    const rejectedBy =
      userInfo && userInfo.userName ? userInfo.userName : "admin";

    const params = {
      comments: comments,
      rejectedBy: rejectedBy,
      rejectionReason: comments,
      transactionId: transactionId || "",
    };

    return this.http.put(`adminWallet/reject/${agentId}`, params, 32);
  }

  // Reject request
  rejectRequest(request: any, comments: string): Observable<any> {
    const requestId = String(
      request && (request.requestId || request.transactionId || "")
    );
    const amount =
      request && request.amount != null ? String(request.amount) : "";
    const recipient =
      (request && (request.phone || request.phoneNumber || request.msisdn)) || "";

    const payload = {
      categoryId: null,
      mfsCommonServiceRequest: {
        mfsTransactionInfo: {
          requestId: requestId,
          timestamp: Date.now(),
        },
      },
      mfsOptionalInfo: null,
      mfsPaybillInfo: null,
      mfsRequestInfo: {
        amount: amount,
        to: String(recipient),
      },
      msisdn: null,
      operatorId: null,
      operatorType: null,
      type: null,
    };

    return this.http
      .post("api/v1/user/transfer/reject_p2p", payload, 35)
      .map((response: any) => {
        if (!response) {
          return response;
        }

        const status = String(response.status || "").toLowerCase();
        const dfsStatus =
          response.dfsCommonServiceResponse &&
          response.dfsCommonServiceResponse.dfsStatusInfo &&
          String(
            response.dfsCommonServiceResponse.dfsStatusInfo.status || ""
          ).toLowerCase();
        const mfsStatus =
          response.mfsCommonServiceResponse &&
          response.mfsCommonServiceResponse.mfsStatusInfo &&
          String(
            response.mfsCommonServiceResponse.mfsStatusInfo.status || ""
          ).toLowerCase();
        const statusInfo =
          response.mfsCommonServiceResponse &&
          response.mfsCommonServiceResponse.mfsStatusInfo;
        const message =
          (statusInfo && statusInfo.errorDescription) ||
          response.message ||
          response.errorDescription ||
          "";

        if (
          status === "success" ||
          response.success === true ||
          response.statusCode === 200 ||
          response.code === 200 ||
          String(response.responseCode || "") === "200" ||
          dfsStatus === "success" ||
          mfsStatus === "success"
        ) {
          return Object.assign({}, response, {
            status: "success",
            message: message,
          });
        }

        return Object.assign({}, response, {
          message: message || response.message || "Failed to reject request",
        });
      });
  }

  sendMoneyTransfer(payload: any): Observable<any> {
    return this.http.post("balanceTransfer/transfer", payload, 32);
  }

  // View receipt - returns image URL
  viewReceipt(receiptFileName: string): Observable<any> {
    // Call actual API endpoint: paymentProf/getPaymentProf/{filename}
    // Endpoint 30 = DFS_USER_MANAGEMENT_ENDPOINT
    // Use 'blob' type to handle image response
    const url = `paymentProf/getPaymentProf/${encodeURIComponent(
      receiptFileName
    )}`;

    // Manually add Authorization header with bearer token (same pattern as other blob requests)
    let token = this.common.token;
    if (!token) {
      token = localStorage.getItem("tkn");
    }
    let headObj: any = {};
    if (token) {
      headObj[this.common.authToken] = token;
    }
    let headers = new Headers(headObj);
    let reqHead = new RequestOptions({
      responseType: ResponseContentType.Blob,
      headers: headers,
    });

    // Make actual HTTP call with auth headers
    // Type 'blob' tells http service to return Blob instead of JSON
    return this.http.get(url, null, 30, reqHead, "blob").map((blob: Blob) => {
      // Create object URL from blob for viewing in browser
      const imageUrl = URL.createObjectURL(blob);
      return {
        status: "success",
        imageUrl: imageUrl,
        fileName: receiptFileName,
        fileType: "image/jpeg",
        blob: blob, // Keep blob reference for cleanup if needed
      };
    });
  }

  // Download receipt - returns blob data
  downloadReceipt(imageName: string): Observable<any> {
    // Call actual API endpoint: paymentProf/getPaymentProf/{filename}
    // Endpoint 30 = DFS_USER_MANAGEMENT_ENDPOINT
    // Use 'blob' type to handle image response
    const url = `paymentProf/getPaymentProf/${encodeURIComponent(imageName)}`;

    // Manually add Authorization header with bearer token (same pattern as other blob requests)
    let token = this.common.token;
    if (!token) {
      token = localStorage.getItem("tkn");
    }
    let headObj: any = {};
    if (token) {
      headObj[this.common.authToken] = token;
    }
    let headers = new Headers(headObj);
    let reqHead = new RequestOptions({
      responseType: ResponseContentType.Blob,
      headers: headers,
    });

    // Make actual HTTP call with auth headers
    // Type 'blob' tells http service to return Blob instead of JSON
    return this.http.get(url, null, 30, reqHead, "blob").map((blob: Blob) => {
      // Extract file extension from imageName for proper download
      const fileExtension = imageName.split(".").pop() || "jpg";
      const fileName = imageName || `receipt.${fileExtension}`;

      return {
        status: "success",
        blob: blob,
        fileName: fileName,
        contentType: `image/${fileExtension === "png" ? "png" : "jpeg"}`,
      };
    });
  }

  getCountByStatus(status: string = "PENDING"): Observable<any> {
    const formData = new FormData();
    formData.append("status", status);

    return Observable.create((observer: any) => {
      const xhr = new XMLHttpRequest();
      const apiEndpoint = environment.MFS_WALLET_MANAGEMENT_ENDPOINT;
      const url = `${apiEndpoint}adminWallet/getCountByStatus`;

      xhr.open("POST", url, true);

      const token = this.common.token || localStorage.getItem("tkn");
      if (token) {
        xhr.setRequestHeader(this.common.authToken, token);
      }
      xhr.setRequestHeader("locale", "en");
      xhr.setRequestHeader("language", "ENG");

      xhr.onreadystatechange = () => {
        if (xhr.readyState === 4) {
          if (xhr.status === 200) {
            try {
              const response = JSON.parse(xhr.responseText);
              observer.next(response);
              observer.complete();
            } catch (e) {
              observer.error(new Error("Failed to parse response"));
            }
          } else {
            try {
              const errorResponse = JSON.parse(xhr.responseText);
              observer.error(errorResponse);
            } catch (e) {
              observer.error(
                new Error(`Request failed with status ${xhr.status}`)
              );
            }
          }
        }
      };

      xhr.onerror = () => {
        observer.error(new Error("Network error"));
      };

      xhr.send(formData);
    });
  }

  updateFailedStatus(agentId: string, transactionId: string): Observable<any> {
    const params = {
      transactionId: transactionId,
    };
    return this.http.put(
      `adminWallet/updateFailedStatus/${agentId}`,
      params,
      32
    );
  }
}



// WEBPACK FOOTER //
// /Users/ldt/Desktop/ARES-admin-frontend/src/app/main/agent-requests/agent-requests.service.ts